import { useState, useRef, useEffect } from 'preact/hooks';

interface ChatInputProps {
  onSend: (message: string) => void;
  disabled?: boolean;
}

export function ChatInput({ onSend, disabled }: ChatInputProps) {
  const [message, setMessage] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (!disabled && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [disabled]);

  const autoResize = () => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 400)}px`;
    }
  };

  const handleSubmit = (e: Event) => {
    e.preventDefault();
    const trimmedMessage = message.trim();
    if (!trimmedMessage || disabled) return;

    onSend(trimmedMessage);
    setMessage('');
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  return (
    <div class="bg-base-100">
      <div class="max-w-4xl mx-auto px-4 pb-6 pt-2">
        {/* Input Container with Border */}
        <form onSubmit={handleSubmit}>
          <div class="border border-base-300 rounded-lg bg-base-100">
            <div class="flex items-end gap-2 p-2">
              {/* Upload Button */}
              <button
                type="button"
                class="btn btn-ghost btn-circle btn-sm"
                aria-label="Upload file"
                disabled={disabled}
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M18.375 12.739l-7.693 7.693a4.5 4.5 0 01-6.364-6.364l10.94-10.94A3 3 0 1119.5 7.372L8.552 18.32m.009-.01l-.01.01m5.699-9.941l-7.81 7.81a1.5 1.5 0 002.112 2.13" />
                </svg>
              </button>

              {/* Textarea */}
              <textarea
                ref={textareaRef}
                name="message"
                placeholder="Type a message..."
                class="textarea flex-1 resize-none border-0 focus:outline-none focus:border-0 p-0 min-h-[24px] max-h-[400px] leading-6"
                style="background-color: transparent; box-shadow: none;"
                rows={1}
                value={message}
                onInput={(e) => {
                  setMessage(e.currentTarget.value);
                  autoResize();
                }}
                onKeyDown={handleKeyDown}
                disabled={disabled}
              />

              {/* Send Button */}
              <button
                type="submit"
                class="btn btn-primary btn-sm btn-circle"
                aria-label="Send message"
                disabled={disabled || !message.trim()}
              >
                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                </svg>
              </button>
            </div>
          </div>
        </form>

        {/* Keyboard hints */}
        <div class="flex justify-between items-center mt-2 px-2">
          <div class="text-xs text-base-content/50">
            Press Enter to send, Shift+Enter for new line
          </div>
          <div class="text-xs text-base-content/50">
            AI can make mistakes. Please verify important information.
          </div>
        </div>
      </div>
    </div>
  );
}
